import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import SoundId, { SoundFiles } from './soundid';
import { useLocalStorage } from 'usehooks-ts';
import { Intent } from '@blueprintjs/core';
import { LocalStorageKeys } from '../../../src/enum/localstorage';
import { Howl, Howler } from 'howler';
import { useToaster } from '../toaster';
import { IconNames } from '@blueprintjs/icons';

const DEFAULT_VOLUME = 0.5;

type SoundManagerContextProps = {
    playSound: (sound: SoundId, params?: SoundManagerPlaySoundParams) => void;
    setVolume: (newVolume: number) => void;
    volume: number;
}

type SoundManagerPlaySoundParams = {
    speed?: number;
    volume?: number;
};

const SoundManagerContext = createContext<SoundManagerContextProps>({
    playSound: () => { },
    setVolume: () => { },
    volume: 1,
});

const getUrlForSound = (filename: string): string => `/sounds/${filename}`;

const getDefaultSound = (soundId: SoundId): string => {
    const enumName: string = SoundId[soundId];
    const filename: string = `${enumName.toLowerCase()}.mp3`;

    return filename;
}

export function SoundManagerProvider({ children }) {
    const toaster = useToaster();
    const audioCache = useRef({});
    const hasShownInteractionWarning = useRef<boolean>(false);
    const errorToastKey = useRef<string>(null);
    const [volume, setVolume] = useLocalStorage<number>(LocalStorageKeys.Volume, DEFAULT_VOLUME);

    // playSound is a useCallback and we want to avoid making a new one
    // every time volume changes, so we use a ref to always have the latest volume
    const volumeRef = useRef<number>(volume);

    useEffect(
        () => {
            volumeRef.current = volume;
        },
        [volume],
    );

    const onPlayError = useCallback(
        () => {
            if (hasShownInteractionWarning.current) {
                return;
            }

            errorToastKey.current = toaster.show({
                icon: IconNames.VolumeUp,
                intent: Intent.WARNING,
                message: 'Cannot play sound, try interacting with the page',
            });

            hasShownInteractionWarning.current = true;
        },
        [toaster],
    );

    const createHowl = useCallback(
        (filename: string): Howl => {
            return new Howl({
                src: [getUrlForSound(filename)],
                onplayerror: onPlayError,
            });
        },
        [onPlayError],
    );

    // Preload sounds and cache them
    useEffect(
        () => {
            for (const soundId of Object.values(SoundId)) {
                // ignore string values of the enum
                if (isNaN(Number(soundId))) {
                    continue;
                }

                const soundFiles = SoundFiles[soundId];

                if (soundFiles === undefined) {
                    const sound = getDefaultSound(soundId as SoundId);
                    audioCache.current[sound] = createHowl(sound);
                } else {
                    for (const sound of soundFiles) {
                        audioCache.current[sound] = createHowl(sound);
                    }
                }
            }
        },
        [createHowl],
    );

    const playSound = useCallback(
        (soundId: SoundId, params?: SoundManagerPlaySoundParams) => {
            let sound: string;

            if (SoundFiles[soundId] === undefined) {
                sound = getDefaultSound(soundId as SoundId);
            } else {
                const soundFiles = SoundFiles[soundId];
                sound = soundFiles[Math.floor(Math.random() * soundFiles.length)];
            }

            const source: Howl | null = audioCache.current[sound];
            if (!source) {
                console.error(`Could not play audio file`, soundId);
                return;
            }

            // source.rate(params?.speed ?? 1);
            source.volume(params?.volume ?? volumeRef.current);
            source.play();

            if (Howler.ctx?.state === 'suspended') {
                setTimeout(
                    () => {
                        if (Howler.ctx?.state === 'suspended') {
                            onPlayError();
                            source.stop();
                        }
                    },
                    100,
                );
            }
        },
        [onPlayError],
    );

    useEffect(
        () => {
            if (!toaster) {
                return () => {};
            }

            const howl = new Howl({
                src: [getUrlForSound('silent.mp3')],
                onunlock: () => {
                    if (errorToastKey.current === null) {
                        return;
                    }

                    toaster.dismiss(errorToastKey.current);
                    errorToastKey.current = null;
                },
            });

            return () => howl.off('unlock');
        },
        [toaster],
    );

    const value = useMemo(
        () => ({
            playSound,
            setVolume,
            volume,
        }),
        [playSound, setVolume, volume],
    );

    return (
        <SoundManagerContext.Provider value={value}>
            {children}
        </SoundManagerContext.Provider>
    );
}

export function useSoundManager() {
    return useContext(SoundManagerContext);
}
